import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { LabMicroList } from '@wodi/shared';
import { Button, Checkbox, Notice, TextField } from '@wodi/ui';
import { useState } from 'react';

import { addMicroItem, microListQuery, updateMicroItem } from '../api/lab-qc.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../lab/lab.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { LabSettingsFrame, QueryState } from './settings-lab.tsx';

// Settings > Lab > Microbiology lists (docs/plans/phase3-lab-qc-and-microbiology.md §1.2.8):
// the organisms and antibiotics the lab records. Names only: codes come from an official file
// when one is loaded (TODO(verify-spec)), never typed here. Entries are switched off, never
// removed, so past cultures keep their words.

const TITLES: Record<LabMicroList, { title: string; one: string }> = {
  organisms: { title: 'Organisms', one: 'organism' },
  antibiotics: { title: 'Antibiotics', one: 'antibiotic' },
};

function MicroList({ list, canEdit }: { list: LabMicroList; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const items = useQuery(microListQuery(list));
  const [name, setName] = useState('');
  const { title, one } = TITLES[list];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['lab', 'catalogue', 'micro'] });
  const add = useMutation({
    mutationFn: () => addMicroItem(list, name.trim()),
    onSuccess: async () => {
      toasts.show({ title: `Added to ${title.toLowerCase()}` });
      setName('');
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateMicroItem(list, id, { status: active ? 'active' : 'inactive' }),
    onSuccess: refresh,
  });
  const id = `micro-${list}`;
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionHeading}>
        {title}
      </h2>
      <QueryState query={items} what={title.toLowerCase()} />
      {items.isSuccess && items.data.items.length === 0 ? (
        <p>{`No ${title.toLowerCase()} yet. Add the ones your lab reports.`}</p>
      ) : null}
      <ul className={styles.list}>
        {(items.data?.items ?? []).map((item) => (
          <li key={item.id} className={styles.listRow}>
            <span>{item.name}</span>
            <span className={styles.meta}>
              {item.code === null ? 'No code loaded' : `${item.code.code} (${item.code.system})`}
            </span>
            <Checkbox
              isSelected={item.status === 'active'}
              isDisabled={!canEdit || !online}
              onChange={(active) => {
                toggle.mutate({ id: item.id, active });
              }}
            >
              {`${item.name} in use`}
            </Checkbox>
          </li>
        ))}
      </ul>
      {canEdit ? (
        <form
          className={styles.toolbar}
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '') add.mutate();
          }}
        >
          <TextField label={`New ${one}`} value={name} onChange={setName} />
          <Button
            type="submit"
            isDisabled={!online || name.trim() === ''}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add
          </Button>
        </form>
      ) : null}
      {add.isError || toggle.isError ? (
        <Notice tone="error">
          {errorText(add.error ?? toggle.error, "The list wasn't saved. Try again.")}
        </Notice>
      ) : null}
    </section>
  );
}

export function LabMicroListsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const canEdit = session !== null && can(session, 'lab.catalogue.manage');
  return (
    <LabSettingsFrame title="Lab settings">
      <Notice tone="info">
        Organism and antibiotic codes are loaded from an official file when one is available. Until
        then entries have names only. Sensitivities are recorded as S, I or R as the lab reads them;
        Wodi does not interpret them.
      </Notice>
      <MicroList list="organisms" canEdit={canEdit} />
      <MicroList list="antibiotics" canEdit={canEdit} />
    </LabSettingsFrame>
  );
}
